// lib/analyse-gabarit.js — LA FORME D'UNE ANALYSE, ET RIEN D'AUTRE.
//
// ⚠️ CE FICHIER EXISTE EN DEUX EXEMPLAIRES OCTET-IDENTIQUES : `lib/` (serveur,
// CommonJS) et `src/lib/` (navigateur — create-react-app refuse tout import
// hors de `src/`, ADR-021). Il ne requiert RIEN : c'est ce qui rend la copie
// stricte possible, et `src/lib/operateur-feuille-parite.test.js` la prouve.
// Il était ESM et vivait dans `src/lib/` seul ; le composeur de feuilles
// (`lib/operateur-feuille.js`) tourne aussi côté serveur, d'où le passage en
// CommonJS — `import { rangerLAnalyse } from` continue de marcher (interop).
//
// ══ LE CARTOUCHE DE DIMITRI, QUI DÉCIDE DE TOUT ════════════════════════════
//
// Sa maquette porte, en tête des analyses : « TOUT LE TEXTE CI-DESSOUS EST
// ÉCRIT À LA MAIN. C'est une démonstration du COMPORTEMENT attendu, pas un
// contenu à copier. AUCUNE DE CES PHRASES NE DOIT SE RETROUVER EN DUR DANS
// L'APPLICATION. »
//
// D'où ce fichier : il porte la STRUCTURE (fixe) et RIEN du contenu (généré,
// à chaque fois, sur la situation du moment). Il n'y a donc pas huit feuilles
// d'analyse dans le produit — il y en a UNE, qui reçoit un objet et le rend.
//
// ══ LES SEPT PARTIES D'UNE ANALYSE, DANS SON ORDRE ═════════════════════════
//
//   1 · le fait, chiffré, avec ce qu'il coûte ou rapporte
//   2 · ce qu'il a regardé, avec un marqueur sur ce qui cloche
//   3 · son raisonnement — ce qui a changé, ce qui n'a pas changé, ce que ça
//       élimine comme explication
//   4 · son avis : « à votre place, je ferais X » — il tranche
//   5 · son pari chiffré, vérifiable plus tard
//   6 · les gestes, séparés en « ce que je peux faire » et « ce qui attend une
//       connexion »
//   7 · ce qu'il ne voit pas
//
// ══ LES NEUF BLOCS D'UNE FEUILLE (les quatre feuilles abouties de la maquette :
//    feuilleCaution, feuillePaiement, feuilleTrou, feuilleRetouches) ══════════
//
//   fait · enjeu (ce que ça coûte OU ce que ça bloque) · systeme (ce que le
//   système fait déjà seul, daté — c'est lui qui DÉCIDE s'il y a un geste) ·
//   regarde (à côté, et ce qui est éliminé) · avis · gestes · pari (avec son
//   seuil d'erreur) · base (sur combien de données, et si c'est peu) ·
//   aveugle. Même moteur, autre liste : `rangerLAnalyse(a, PARTIES_FEUILLE)`.
//
// ══ CE QUI REND CE GABARIT UTILE : IL SAIT ÊTRE INCOMPLET ══════════════════
//
// ⚠️ AUCUNE PARTIE N'EST OBLIGATOIRE ICI, ET UNE PARTIE ABSENTE SE DIT. C'est
// le point dur. Un gabarit qui REMPLIRAIT les trous produirait exactement ce
// que le cartouche interdit : des phrases d'humeur et des affirmations
// générales non vérifiées. Donc : une partie sans matière ne s'affiche pas,
// SAUF « ce qu'il ne voit pas » — qui absorbe ce qui manque et le NOMME. C'est
// la partie qui transforme un trou en information. (Les obligations propres à
// une FEUILLE — le fait sourcé, l'enjeu, l'automate déclaré, la base — sont
// tenues par le composeur, pas par le gabarit : lui range, il ne juge pas.)

const PARTIES = Object.freeze([
  "fait",
  "regarde",
  "raisonnement",
  "avis",
  "pari",
  "gestes",
  "aveugle",
]);

const PARTIES_FEUILLE = Object.freeze([
  "fait",
  "enjeu",
  "systeme",
  "regarde",
  "avis",
  "gestes",
  "pari",
  "base",
  "aveugle",
]);

/**
 * Une valeur « sans matière ». Une chaîne blanche, une liste vide, un objet
 * dont TOUTES les valeurs sont elles-mêmes sans matière (un `gestes` dont
 * `peut` et `attend` sont vides, un `enjeu` sans coût ni blocage).
 */
function _sansMatiere(v) {
  if (v == null) return true;
  if (typeof v === "string") return !v.trim();
  if (typeof v === "number") return !Number.isFinite(v);
  if (typeof v === "boolean") return false;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") {
    const cles = Object.keys(v);
    return cles.length === 0 || cles.every((k) => _sansMatiere(v[k]));
  }
  return false;
}

/**
 * Range une analyse dans la forme, et dit ce qui manque.
 *
 * ⚠️ IL NE FABRIQUE RIEN. Ce module ne compose aucune phrase : il reçoit ce
 * qu'un lecteur a produit et le range. Un gabarit qui écrirait « pas de
 * données » à la place d'une partie absente serait déjà du contenu en dur.
 *
 * @param {object} a — les parties, toutes facultatives
 * @param {readonly string[]} [parties] — la liste des parties et leur ordre
 *   (`PARTIES` par défaut ; `PARTIES_FEUILLE` pour une feuille)
 * @returns {{parties:Array, manquantes:string[], utilisable:boolean}}
 */
function rangerLAnalyse(a = {}, parties = PARTIES) {
  const rangees = [];
  const manquantes = [];
  for (const nom of parties) {
    const v = a[nom];
    if (_sansMatiere(v)) manquantes.push(nom);
    else rangees.push({ nom, valeur: v });
  }
  return {
    parties: rangees,
    manquantes,
    // ⚠️ UNE ANALYSE SANS SON FAIT N'EST PAS UNE ANALYSE. C'est la seule partie
    // dont l'absence disqualifie : sans elle, les autres commentent un fait
    // que personne n'a énoncé.
    utilisable: !manquantes.includes("fait"),
  };
}

/**
 * La phrase de la partie « ce qu'il ne voit pas », composée à partir de ce
 * qui manque.
 *
 * ⚠️ ELLE DIT POURQUOI, PAS SEULEMENT QUOI. « Je ne vois pas votre historique »
 * n'apprend rien ; « je ne peux pas comparer à l'an dernier : vos données
 * commencent en janvier » dit à Dimitri quand ça deviendra possible.
 */
const RAISONS_ABSENCE = Object.freeze({
  raisonnement:
    "je ne sais pas encore dire POURQUOI : il me faudrait comparer à une période semblable, et l'historique commence en janvier 2026",
  pari: "je ne peux pas encore faire de pari vérifiable sur ce point : il faut des gestes posés et relus, et le compteur vient de démarrer",
  avis: "je n'ai pas d'avis tranché à vous donner sur ce point",
  regarde: "je n'ai pas pu détailler ce que j'ai regardé",
});

function phraseDeLAveugle(manquantes, aveugleDejaDit) {
  const dits = [];
  if (typeof aveugleDejaDit === "string" && aveugleDejaDit.trim())
    dits.push(aveugleDejaDit.trim());
  else if (Array.isArray(aveugleDejaDit))
    for (const d of aveugleDejaDit)
      if (typeof d === "string" && d.trim()) dits.push(d.trim());
  for (const m of manquantes || [])
    if (RAISONS_ABSENCE[m]) dits.push(RAISONS_ABSENCE[m]);
  return dits.length ? dits.join(" · ") : "";
}

module.exports = {
  PARTIES,
  PARTIES_FEUILLE,
  RAISONS_ABSENCE,
  rangerLAnalyse,
  phraseDeLAveugle,
  _internal: { _sansMatiere },
};
